import React from 'react';
import classNames from 'classnames';

import {IconButton} from '@albert-io/atomic';

import {useCalculatorWindowStore} from './calculatorWindow.store';
import {useIsCalculatorAvailable} from './useIsCalculatorAvailable';

interface CalculatorToggleProps {
  className?: string;
  /** The resting color, matched to the toolbar it sits in. The open state is always brand. */
  color: React.ComponentProps<typeof IconButton>['color'];
  'data-testid': string;
}

/**
 * Opens and closes the in-app calculator window. The only entry point to the
 * calculator, and it shares `useIsCalculatorAvailable` with the window itself
 * so the button can never offer something the window would refuse to show.
 */
export default function CalculatorToggle({
  className,
  color,
  'data-testid': testId
}: CalculatorToggleProps) {
  const isCalculatorAvailable = useIsCalculatorAvailable();
  const isOpen = useCalculatorWindowStore((state) => state.isOpen);
  const toggle = useCalculatorWindowStore((state) => state.toggle);

  if (!isCalculatorAvailable) {
    return null;
  }

  return (
    <IconButton
      aria-pressed={isOpen}
      // Only the open state is designed; the resting state matches its
      // neighbours on the pill. The open fill is Figma's Background/brand.
      className={classNames(className, {
        'background-brand! hover:background-brand-bold-hover!': isOpen
      })}
      color={isOpen ? 'primary' : color}
      data-testid={testId}
      icon='calculator'
      iconStyle='regular'
      label='Calculator'
      onClick={toggle}
      tooltip='Calculator'
      tooltipProps={{placement: 'bottom'}}
      variant={isOpen ? 'solid' : undefined}
    />
  );
}
